// Pocket Mustang Preserve v3.0 - Enhanced Card System
// New mechanics: Reputation, Seasons, Crisis Events

export type Trait = 'dun' | 'grulla' | 'buckskin' | 'leader' | 'hardy' | 'swift' | 'gentle' | 'wild' | null;
export type Rarity = 'common' | 'uncommon' | 'rare' | 'legendary';
export type CardType = 'horse' | 'action' | 'event' | 'infrastructure' | 'legendary' | 'crisis';

export interface CardData {
  id: string;
  title: string;
  art: string;
  description: string;
  type: CardType;
  rarity: Rarity;
  trait?: Trait;
  cost?: number;
  effect: (resources: GameResources) => void;
  bio?: string; // Flavor text for legendaries
  isCrisis?: boolean; // Must be played immediately
  requiresUnlock?: boolean; // Only available after first playthrough (e.g., paint horses)
  spawnColor?: 'grulla' | 'buckskin' | 'bay' | 'roan' | 'sorrel' | 'black' | 'paint'; // Horse color for spawning
  spawnCount?: number; // How many horses to spawn visually
  spawnBurro?: boolean; // Spawns a burro instead of horse
}

export interface GameResources {
  population: number;
  forage: number;
  water: number;
  funds: number;
  year: number;
  foalRate: number;
  diversity: number;
  // NEW MECHANICS
  reputation: number;    // 0-100: Public support affects funding
  herdHealth: number;    // 0-100: Overall herd condition
  blmRelations: number;  // 0-100: Government cooperation level
}

// Trait bonuses applied during gameplay
export const TRAIT_BONUSES: Record<Trait & string, { stat: string; bonus: number; desc: string }> = {
  'dun': { stat: 'water', bonus: 15, desc: '+15% water retention' },
  'grulla': { stat: 'forage', bonus: 10, desc: '+10% forage efficiency' },
  'buckskin': { stat: 'funds', bonus: 500, desc: 'Finds $500 in grants' },
  'leader': { stat: 'diversity', bonus: 20, desc: '+20 herd diversity' },
  'hardy': { stat: 'forage', bonus: 8, desc: 'Survives on less forage' },
  'swift': { stat: 'water', bonus: 10, desc: 'Finds water sources' },
  'gentle': { stat: 'funds', bonus: 800, desc: 'Easy to adopt out' },
  'wild': { stat: 'diversity', bonus: 15, desc: 'Strong wild genes' },
};

// ============================================
// STARTER DECK - Pryor Mountains (24 cards)
// ============================================
export const pryorStarterDeck: CardData[] = [
  // === COMMON HORSE CARDS (Generic) ===
  {
    id: "add_mare",
    title: "Add Mare",
    art: "🐴",
    description: "A mare joins the herd. +1 pop",
    type: "horse",
    rarity: "common",
    effect: (r) => { r.population += 1; }
  },
  {
    id: "add_stallion",
    title: "Add Stallion",
    art: "🐴",
    description: "A stallion joins the herd. +1 pop, +10 diversity",
    type: "horse",
    rarity: "common",
    effect: (r) => { r.population += 1; r.diversity += 10; }
  },
  {
    id: "add_yearling",
    title: "Add Yearling",
    art: "🐴",
    description: "A young yearling joins the herd. +1 pop",
    type: "horse",
    rarity: "common",
    effect: (r) => { r.population += 1; }
  },
  {
    id: "add_foal",
    title: "Add Foal",
    art: "🐴",
    description: "An orphan foal is rescued. +1 pop, +5 reputation",
    type: "horse",
    rarity: "common",
    effect: (r) => { r.population += 1; r.reputation = Math.min(100, r.reputation + 5); }
  },

  // === UNCOMMON HORSE CARDS ===
  {
    id: "add_band",
    title: "Add Band",
    art: "🐴",
    description: "A small family band joins. +3 pop",
    type: "horse",
    rarity: "uncommon",
    cost: 1500,
    effect: (r) => { r.population += 3; r.funds -= 1500; }
  },
  {
    id: "add_lead_mare",
    title: "Add Lead Mare",
    art: "🐴",
    description: "Experienced lead mare guides herd. +1 pop, +10 forage",
    type: "horse",
    rarity: "uncommon",
    effect: (r) => { r.population += 1; r.forage = Math.min(100, r.forage + 10); }
  },
  {
    id: "add_bachelor_band",
    title: "Bachelor Band",
    art: "🐴",
    description: "Young stallions join. +2 pop, +15 diversity",
    type: "horse",
    rarity: "uncommon",
    effect: (r) => { r.population += 2; r.diversity += 15; }
  },
  {
    id: "add_nursing_mare",
    title: "Nursing Mare",
    art: "🐴",
    description: "Mare with foal joins. +2 pop, +5 herd health",
    type: "horse",
    rarity: "uncommon",
    effect: (r) => { r.population += 2; r.herdHealth = Math.min(100, r.herdHealth + 5); }
  },

  // === ACTION CARDS ===
  {
    id: "pzp_vaccine",
    title: "PZP Vaccine",
    art: "💉",
    description: "Fertility control. Halves foaling rate this year",
    type: "action",
    rarity: "common",
    cost: 800,
    effect: (r) => { r.foalRate *= 0.5; r.funds -= 800; }
  },
  {
    id: "adoption_drive",
    title: "Adoption Drive",
    art: "🚚❤️",
    description: "Find homes for up to 3 horses (min 20 kept). Earn $1000 each",
    type: "action",
    rarity: "common",
    effect: (r) => {
      const adopted = Math.min(3, Math.max(0, r.population - 20));
      r.population -= adopted;
      r.funds += adopted * 1000;
      r.reputation = Math.min(100, r.reputation + adopted * 3);
    }
  },
  {
    id: "gentling_program",
    title: "Gentling Program",
    art: "🤝🐴",
    description: "Train 2 horses for adoption. -2 pop, +$3500",
    type: "action",
    rarity: "uncommon",
    cost: 1000,
    effect: (r) => {
      if (r.population >= 22) {
        r.population -= 2;
        r.funds += 3500 - 1000;
        r.reputation = Math.min(100, r.reputation + 5);
      }
    }
  },
  {
    id: "range_survey",
    title: "Range Survey",
    art: "🗺️",
    description: "Assess land health. +15 forage",
    type: "action",
    rarity: "common",
    cost: 500,
    effect: (r) => { 
      r.forage = Math.min(100, r.forage + 15); 
      r.funds -= 500;
    }
  },
  {
    id: "volunteer_day",
    title: "Volunteer Day",
    art: "👥",
    description: "Community helps with range work. +$2000",
    type: "action",
    rarity: "common",
    effect: (r) => { r.funds += 2000; }
  },
  {
    id: "photo_tour",
    title: "Wild Horse Photo Tour",
    art: "📸",
    description: "Eco-tourism brings in funds. +$3000",
    type: "action",
    rarity: "common",
    effect: (r) => { r.funds += 3000; }
  },
  {
    id: "hay_drop",
    title: "Emergency Hay Drop",
    art: "🌾",
    description: "Supplement winter forage. +25 forage",
    type: "action",
    rarity: "uncommon",
    cost: 2000,
    effect: (r) => { r.forage = Math.min(100, r.forage + 25); r.funds -= 2000; }
  },
  {
    id: "water_haul",
    title: "Water Haul",
    art: "🚿",
    description: "Emergency water delivery. +20 water",
    type: "action",
    rarity: "common",
    cost: 1500,
    effect: (r) => { r.water = Math.min(100, r.water + 20); r.funds -= 1500; }
  },

  // === INFRASTRUCTURE CARDS ===
  {
    id: "guzzler",
    title: "Install Guzzler",
    art: "💧",
    description: "Rainwater collection system. +30 water",
    type: "infrastructure",
    rarity: "uncommon",
    cost: 3000,
    effect: (r) => { r.water = Math.min(100, r.water + 30); r.funds -= 3000; }
  },
  {
    id: "spring_restoration",
    title: "Spring Restoration",
    art: "🌊",
    description: "Restore natural spring. +25 water",
    type: "infrastructure",
    rarity: "rare",
    cost: 4000,
    effect: (r) => { r.water = Math.min(100, r.water + 25); r.funds -= 4000; }
  },
  {
    id: "fence_removal",
    title: "Fence Removal",
    art: "🚧",
    description: "Open up grazing corridors. +15 forage",
    type: "infrastructure",
    rarity: "common",
    cost: 1500,
    effect: (r) => { r.forage = Math.min(100, r.forage + 15); r.funds -= 1500; }
  },
  {
    id: "observation_blind",
    title: "Observation Blind",
    art: "🏕️",
    description: "Research station brings grants. -$1500, +10 reputation",
    type: "infrastructure",
    rarity: "uncommon",
    cost: 1500,
    effect: (r) => { r.funds -= 1500; r.reputation = Math.min(100, r.reputation + 10); }
  },
  {
    id: "salt_lick",
    title: "Mineral Salt Lick",
    art: "🧂",
    description: "Improves herd health. +10 diversity",
    type: "infrastructure",
    rarity: "common",
    cost: 800,
    effect: (r) => { r.diversity += 10; r.funds -= 800; }
  },

  // === EVENT CARDS (negative - gentle) ===
  {
    id: "drought",
    title: "DROUGHT!",
    art: "☀️🔥",
    description: "Scorching summer. -20 forage, -15 water",
    type: "event",
    rarity: "common",
    effect: (r) => { 
      r.forage = Math.max(0, r.forage - 20); 
      r.water = Math.max(0, r.water - 15); 
    }
  },
  {
    id: "harsh_winter",
    title: "Harsh Winter",
    art: "❄️🌨️",
    description: "Brutal cold snap. -8% population",
    type: "event",
    rarity: "uncommon",
    effect: (r) => { 
      r.population = Math.max(1, Math.floor(r.population * 0.92)); 
    }
  },
  {
    id: "wildfire",
    title: "Wildfire",
    art: "🔥🌲",
    description: "Range fire damages grazing. -25 forage",
    type: "event",
    rarity: "uncommon",
    effect: (r) => { r.forage = Math.max(0, r.forage - 25); }
  },
  {
    id: "budget_cuts",
    title: "Budget Cuts",
    art: "📉",
    description: "Federal funding reduced. -$2500",
    type: "event",
    rarity: "common",
    effect: (r) => { r.funds -= 2500; }
  },

  // === EVENT CARDS (positive - 60% of events) ===
  {
    id: "good_rains",
    title: "Good Rains",
    art: "🌧️🌈",
    description: "Wet season! +20 water, +15 forage",
    type: "event",
    rarity: "common",
    effect: (r) => { 
      r.water = Math.min(100, r.water + 20); 
      r.forage = Math.min(100, r.forage + 15); 
    }
  },
  {
    id: "celebrity_visit",
    title: "Celebrity Visit",
    art: "⭐",
    description: "Famous advocate visits. +$5000 donation",
    type: "event",
    rarity: "uncommon",
    effect: (r) => { r.funds += 5000; }
  },
  {
    id: "grant_awarded",
    title: "Conservation Grant",
    art: "🏆",
    description: "Research grant awarded. +$4000",
    type: "event",
    rarity: "uncommon",
    effect: (r) => { r.funds += 4000; }
  },
  {
    id: "mild_winter",
    title: "Mild Winter",
    art: "🌤️",
    description: "Easy winter. +10 forage, +5 water",
    type: "event",
    rarity: "common",
    effect: (r) => { 
      r.forage = Math.min(100, r.forage + 10); 
      r.water = Math.min(100, r.water + 5); 
    }
  }
];

// ============================================
// LEGENDARY CARDS - Real Pryor Mountain Horses
// ============================================
export const legendaryCards: CardData[] = [
  {
    id: "cloud_king",
    title: "Cloud the Stallion",
    art: "👑🐴☁️",
    description: "The legendary pale stallion of the Pryors. +2 pop, +25 diversity",
    type: "legendary",
    rarity: "legendary",
    trait: "leader",
    effect: (r) => { r.population += 2; r.diversity += 25; },
    bio: "Cloud was born in 1995 and became the most famous wild horse in America. Documented by Ginger Kathrens, he sired dozens of foals and ruled the Pryors for over a decade."
  },
  {
    id: "flint",
    title: "Flint (Cloud's Son)",
    art: "🪨🐴",
    description: "Hardy son of Cloud, carries the legacy. +1 pop, +10 forage",
    type: "legendary",
    rarity: "legendary",
    trait: "hardy",
    effect: (r) => { r.population += 1; r.forage = Math.min(100, r.forage + 10); },
    bio: "Flint inherited his father's strength and became a band stallion in his own right. His hardy nature helped his band survive harsh Pryor winters."
  },
  {
    id: "galaxy",
    title: "Galaxy",
    art: "🌌🐴",
    description: "Stunning grulla mare with primitive markings. +1 pop, +15 water",
    type: "legendary",
    rarity: "legendary",
    trait: "dun",
    effect: (r) => { r.population += 1; r.water = Math.min(100, r.water + 15); },
    bio: "Galaxy's striking grulla coloring and dorsal stripe mark her as a true Spanish mustang descendant. She knows every water source in the range."
  },
  {
    id: "bolder",
    title: "Bolder",
    art: "💪🐴",
    description: "Dominant dun stallion, fierce protector. +2 pop, +10 diversity",
    type: "legendary",
    rarity: "legendary",
    trait: "wild",
    effect: (r) => { r.population += 2; r.diversity += 10; },
    bio: "Bolder earned his name through countless battles to protect his band. His wild spirit and dun coloring represent the heart of the Pryors."
  },
  {
    id: "echo_spirit",
    title: "Echo's Spirit",
    art: "👻💫",
    description: "The spirit of lost horses returns. Revive 3 lost population",
    type: "legendary",
    rarity: "legendary",
    trait: "gentle",
    effect: (r) => { r.population += 3; r.diversity += 5; },
    bio: "Echo was Cloud's grandson, gentle and beloved. Though he passed too young, his spirit lives on in every foal born to the Pryors."
  },
  {
    id: "sitka",
    title: "Sitka",
    art: "🌲🐴",
    description: "Wise old mare, knows all the trails. +15 forage, +10 water",
    type: "legendary",
    rarity: "legendary",
    trait: "buckskin",
    effect: (r) => { r.forage = Math.min(100, r.forage + 15); r.water = Math.min(100, r.water + 10); },
    bio: "Sitka has led her band through 15 winters. She knows every hidden meadow and secret spring in the Pryor Mountains."
  },
];

// ============================================
// RARE CARDS - Special moments
// ============================================
export const rareCards: CardData[] = [
  {
    id: "twin_foals",
    title: "Twin Foals!",
    art: "🐴🐴✨",
    description: "Rare twin birth! +2 pop, +20 diversity",
    type: "horse",
    rarity: "rare",
    trait: "wild",
    effect: (r) => { r.population += 2; r.diversity += 20; }
  },
  {
    id: "documentary_crew",
    title: "Documentary Crew",
    art: "🎬📺",
    description: "Film crew documents your herd. +$4000, +10 reputation",
    type: "action",
    rarity: "rare",
    effect: (r) => { r.funds += 4000; r.reputation = Math.min(100, r.reputation + 10); }
  },
  {
    id: "sanctuary_partnership",
    title: "Sanctuary Partnership",
    art: "🏠❤️",
    description: "Partner with sanctuary. +$3500, +10 diversity",
    type: "action",
    rarity: "rare",
    effect: (r) => { r.funds += 3500; r.diversity += 10; }
  },
  {
    id: "ancient_spring",
    title: "Ancient Spring Found",
    art: "🗿💧",
    description: "Discover hidden water source. +35 water",
    type: "infrastructure",
    rarity: "rare",
    cost: 2000,
    effect: (r) => { r.water = Math.min(100, r.water + 35); r.funds -= 2000; }
  },
];

// ============================================
// SILLY/FUN EVENT CARDS
// ============================================
export const sillyEventCards: CardData[] = [
  {
    id: "viral_foal",
    title: "Foal Goes Viral! 📱",
    art: "🐴📱✨",
    description: "Baby horse TikTok fame! +$3000, +10 reputation",
    type: "event",
    rarity: "uncommon",
    effect: (r) => { r.funds += 3000; r.reputation = Math.min(100, r.reputation + 10); }
  },
  {
    id: "burro_bff",
    title: "Burro BFF Joins!",
    art: "🫏❤️",
    description: "A friendly burro adopts your herd! +10 forage",
    type: "event",
    rarity: "rare",
    spawnBurro: true,
    effect: (r) => { r.forage = Math.min(100, r.forage + 10); }
  },
  {
    id: "burro_adoption",
    title: "Burro Adoption Day",
    art: "🫏🚚",
    description: "Families want burros! Adopt out 2 burros for $1500",
    type: "action",
    rarity: "uncommon",
    effect: (r) => { r.funds += 1500; }
  },
  {
    id: "horse_whisperer",
    title: "Horse Whisperer Visit",
    art: "🧙‍♀️🐴",
    description: "Mysterious visitor calms the herd. +15 diversity",
    type: "event",
    rarity: "uncommon",
    effect: (r) => { r.diversity += 15; }
  },
  {
    id: "rainbow_foal",
    title: "Rainbow After Storm",
    art: "🌈🐴",
    description: "Beautiful omen! +10 all resources",
    type: "event",
    rarity: "rare",
    effect: (r) => { 
      r.forage = Math.min(100, r.forage + 10);
      r.water = Math.min(100, r.water + 10);
      r.funds += 1000;
      r.diversity += 10;
    }
  },
  {
    id: "school_field_trip",
    title: "School Field Trip",
    art: "🚌👧👦",
    description: "Kids learn about mustangs! +$2000, +5 diversity",
    type: "event",
    rarity: "common",
    effect: (r) => { r.funds += 2000; r.diversity += 5; }
  },
];

// ============================================
// SEASONAL EVENT CARDS
// ============================================
export const seasonalEventCards: CardData[] = [
  {
    id: "spring_bloom",
    title: "Spring Bloom 🌸",
    art: "🌸🌷🐴",
    description: "Wildflowers everywhere! +15 forage, +10 water",
    type: "event",
    rarity: "common",
    effect: (r) => { 
      r.forage = Math.min(100, r.forage + 15);
      r.water = Math.min(100, r.water + 10);
    }
  },
  {
    id: "summer_abundance",
    title: "Summer Abundance",
    art: "☀️🌿",
    description: "Peak grazing season! +20 forage",
    type: "event",
    rarity: "common",
    effect: (r) => { r.forage = Math.min(100, r.forage + 20); }
  },
  {
    id: "autumn_gathering",
    title: "Autumn Gathering",
    art: "🍂🐴",
    description: "Bands gather for winter. +10 diversity",
    type: "event",
    rarity: "common",
    effect: (r) => { r.diversity += 10; }
  },
  {
    id: "winter_huddle",
    title: "Winter Huddle",
    art: "❄️🐴🐴",
    description: "Horses keep each other warm. No losses this winter!",
    type: "event",
    rarity: "uncommon",
    effect: (r) => { r.forage = Math.min(100, r.forage + 5); }
  },
];

// ============================================
// CHOICE EVENT CARDS (Special decisions)
// ============================================
export interface ChoiceEventData {
  id: string;
  title: string;
  art: string;
  description: string;
  choices: {
    text: string;
    effect: (r: GameResources) => void;
    result: string;
  }[];
  condition?: (r: GameResources) => boolean;
}

export const choiceEvents: ChoiceEventData[] = [
  {
    id: "shady_trailer",
    title: "Suspicious Trailer...",
    art: "🚛❓",
    description: "A shady BLM contractor offers quick cash for 'excess' horses...",
    condition: (r) => r.year >= 2027 && r.funds < 8000,
    choices: [
      {
        text: "Take the money (+$8000, -5 pop, -20 diversity)",
        effect: (r) => { r.funds += 8000; r.population -= 5; r.diversity -= 20; },
        result: "The horses disappear... You feel uneasy."
      },
      {
        text: "Report them! (+15 diversity, Hero Badge)",
        effect: (r) => { r.diversity += 15; r.funds += 1000; },
        result: "🦸 HERO! You protected the herd and earned recognition!"
      }
    ]
  },
  {
    id: "wild_bachelor",
    title: "Wild Bachelor Appears!",
    art: "🐴💨",
    description: "A wild bachelor stallion wants to join. He's untested but spirited!",
    choices: [
      {
        text: "Welcome him (+2 pop, +15 diversity, -5 forage)",
        effect: (r) => { r.population += 2; r.diversity += 15; r.forage = Math.max(0, r.forage - 5); },
        result: "The bachelor brings new blood to the herd!"
      },
      {
        text: "Let him pass (no change)",
        effect: () => {},
        result: "He gallops off into the sunset..."
      }
    ]
  },
];

// ============================================
// KIGER HMA CARDS (Unlockable)
// ============================================
export const kigerCards: CardData[] = [
  {
    id: "kiger_mesteno",
    title: "Kiger Mesteño",
    art: "🏜️🐴",
    description: "Legendary dun stallion of the Kigers. +2 pop, +20 diversity",
    type: "legendary",
    rarity: "legendary",
    trait: "dun",
    effect: (r) => { r.population += 2; r.diversity += 20; },
    bio: "The Kiger Mesteño inspired the DreamWorks movie 'Spirit'. His dun coloring and primitive markings are prized worldwide."
  },
  {
    id: "kiger_dun_mare",
    title: "Kiger Dun Mare",
    art: "🟫🐴",
    description: "Pure Kiger dun with dorsal stripe. +1 pop, +10 water",
    type: "horse",
    rarity: "uncommon",
    trait: "dun",
    effect: (r) => { r.population += 1; r.water = Math.min(100, r.water + 10); }
  },
  {
    id: "high_desert_grazing",
    title: "High Desert Grazing",
    art: "🏜️🌿",
    description: "Kigers thrive in sparse terrain. +15 forage",
    type: "action",
    rarity: "common",
    effect: (r) => { r.forage = Math.min(100, r.forage + 15); }
  },
];

// ============================================
// McCULLOUGH PEAKS HMA CARDS (Unlockable)
// ============================================
export const mcculloughCards: CardData[] = [
  {
    id: "wild_bunch_band",
    title: "Wild Bunch Band",
    art: "🎨🐴",
    description: "Colorful band joins! +4 pop, +25 diversity",
    type: "legendary",
    rarity: "legendary",
    trait: "wild",
    effect: (r) => { r.population += 4; r.diversity += 25; },
    bio: "The Wild Bunch of McCullough Peaks are known for their incredible color variety - paints, pintos, and every shade imaginable."
  },
  {
    id: "paint_stallion",
    title: "Paint Stallion",
    art: "🎨🐴",
    description: "Striking paint pattern. +2 pop, +$2000 (tourism)",
    type: "horse",
    rarity: "uncommon",
    trait: "gentle",
    requiresUnlock: true, // Only available after first playthrough
    effect: (r) => { r.population += 2; r.funds += 2000; }
  },
  {
    id: "color_genetics",
    title: "Color Genetics Study",
    art: "🧬🎨",
    description: "Researchers study your colorful herd. +$4000, +10 diversity",
    type: "action",
    rarity: "uncommon",
    effect: (r) => { r.funds += 4000; r.diversity += 10; }
  },
];

// ============================================
// CRISIS CARDS - Must be dealt with!
// ============================================
export const crisisCards: CardData[] = [
  {
    id: "drought_crisis",
    title: "Severe Drought",
    art: "🏜️☀️",
    description: "Water sources drying up! -30 water, -15 forage. Pay $3000 to haul water or lose 5 horses.",
    type: "crisis",
    rarity: "rare",
    isCrisis: true,
    cost: 3000,
    effect: (r) => { 
      r.water = Math.max(0, r.water - 30);
      r.forage = Math.max(0, r.forage - 15);
      if (r.funds >= 3000) {
        r.funds -= 3000;
        r.water = Math.min(100, r.water + 20);
      } else {
        r.population = Math.max(5, r.population - 5);
        r.herdHealth = Math.max(0, r.herdHealth - 20);
      }
    }
  },
  {
    id: "blm_roundup",
    title: "BLM Roundup Notice",
    art: "🚁📋",
    description: "Herd over AML! Lose 10 horses to holding or pay $5000 legal fees to delay.",
    type: "crisis",
    rarity: "rare",
    isCrisis: true,
    cost: 5000,
    effect: (r) => {
      if (r.population > 120) {
        if (r.funds >= 5000 && r.blmRelations >= 40) {
          r.funds -= 5000;
          r.blmRelations = Math.max(0, r.blmRelations - 15);
        } else {
          r.population = Math.max(20, r.population - 10);
          r.reputation = Math.max(0, r.reputation - 10);
        }
      }
    }
  },
  {
    id: "wildfire_threat",
    title: "Wildfire Approaching",
    art: "🔥🌲",
    description: "Fire threatens the range! -25 forage. Horses scatter, -10 herd health.",
    type: "crisis",
    rarity: "rare",
    isCrisis: true,
    effect: (r) => {
      r.forage = Math.max(0, r.forage - 25);
      r.herdHealth = Math.max(0, r.herdHealth - 10);
      r.water = Math.max(0, r.water - 10);
    }
  },
  {
    id: "disease_outbreak",
    title: "Strangles Outbreak",
    art: "🦠🐴",
    description: "Contagious disease! Pay $4000 for vet care or lose 3 horses and -20 health.",
    type: "crisis",
    rarity: "rare",
    isCrisis: true,
    cost: 4000,
    effect: (r) => {
      if (r.funds >= 4000) {
        r.funds -= 4000;
        r.herdHealth = Math.max(0, r.herdHealth - 5);
      } else {
        r.population = Math.max(5, r.population - 3);
        r.herdHealth = Math.max(0, r.herdHealth - 20);
        r.diversity = Math.max(0, r.diversity - 5);
      }
    }
  },
  {
    id: "funding_cut",
    title: "Federal Funding Cut",
    art: "💸📉",
    description: "Budget crisis! Lose 40% of current funds. BLM relations -15.",
    type: "crisis",
    rarity: "uncommon",
    isCrisis: true,
    effect: (r) => {
      r.funds = Math.floor(r.funds * 0.6);
      r.blmRelations = Math.max(0, r.blmRelations - 15);
    }
  },
  {
    id: "harsh_winter",
    title: "Brutal Winter",
    art: "❄️🌨️",
    description: "Extreme cold snap! -20 forage, -15 water. Weak horses may not survive.",
    type: "crisis",
    rarity: "uncommon",
    isCrisis: true,
    effect: (r) => {
      r.forage = Math.max(0, r.forage - 20);
      r.water = Math.max(0, r.water - 15);
      if (r.herdHealth < 50) {
        r.population = Math.max(5, r.population - 2);
      }
      r.herdHealth = Math.max(0, r.herdHealth - 10);
    }
  },
  {
    id: "pr_disaster",
    title: "Negative Press Coverage",
    art: "📰😠",
    description: "Bad publicity! -20 reputation. Donations dry up, -$2000.",
    type: "crisis",
    rarity: "uncommon",
    isCrisis: true,
    effect: (r) => {
      r.reputation = Math.max(0, r.reputation - 20);
      r.funds = Math.max(0, r.funds - 2000);
    }
  },
];

// ============================================
// NEW MECHANIC CARDS - Reputation & Relations
// ============================================
export const mechanicCards: CardData[] = [
  {
    id: "documentary_film",
    title: "Documentary Feature",
    art: "🎬🐴",
    description: "Film crew documents your work. +20 reputation, +$4000",
    type: "action",
    rarity: "rare",
    effect: (r) => { r.reputation = Math.min(100, r.reputation + 20); r.funds += 4000; }
  },
  {
    id: "blm_partnership",
    title: "BLM Partnership",
    art: "🤝🏛️",
    description: "Improve government relations. +20 BLM relations, +$1500 grant",
    type: "action",
    rarity: "uncommon",
    cost: 1500,
    effect: (r) => { r.blmRelations = Math.min(100, r.blmRelations + 20); r.funds += 0; }
  },
  {
    id: "vet_checkup",
    title: "Herd Health Check",
    art: "👨‍⚕️🐴",
    description: "Veterinary inspection. +15 herd health, identify issues early.",
    type: "action",
    rarity: "common",
    cost: 2000,
    effect: (r) => { r.herdHealth = Math.min(100, r.herdHealth + 15); r.funds -= 2000; }
  },
  {
    id: "social_media",
    title: "Viral Social Post",
    art: "📱✨",
    description: "Your horses go viral! +15 reputation, +$2500 donations",
    type: "action",
    rarity: "common",
    effect: (r) => { r.reputation = Math.min(100, r.reputation + 15); r.funds += 2500; }
  },
  {
    id: "lobby_congress",
    title: "Congressional Lobby",
    art: "🏛️📜",
    description: "Advocate for wild horses. +15 BLM relations, -$2000",
    type: "action",
    rarity: "uncommon",
    cost: 2000,
    effect: (r) => { r.blmRelations = Math.min(100, r.blmRelations + 15); r.funds -= 2000; }
  },
  {
    id: "sanctuary_tour",
    title: "Sanctuary Tours",
    art: "🚌👀",
    description: "Open to visitors. +10 reputation, +$3500",
    type: "action",
    rarity: "common",
    effect: (r) => { r.reputation = Math.min(100, r.reputation + 10); r.funds += 3500; }
  },
  {
    id: "nutrition_program",
    title: "Nutrition Program",
    art: "🥕🌾",
    description: "Supplement feeding program. +20 herd health, +10 forage",
    type: "action",
    rarity: "uncommon",
    cost: 2500,
    effect: (r) => { 
      r.herdHealth = Math.min(100, r.herdHealth + 20); 
      r.forage = Math.min(100, r.forage + 10);
      r.funds -= 2500;
    }
  },
  {
    id: "celebrity_visit",
    title: "Celebrity Endorsement",
    art: "⭐🎤",
    description: "Famous advocate visits! +15 reputation, +$3000 donations",
    type: "action",
    rarity: "rare",
    effect: (r) => { r.reputation = Math.min(100, r.reputation + 15); r.funds += 3000; }
  },
  {
    id: "research_grant",
    title: "University Research Grant",
    art: "🎓📊",
    description: "Partner with researchers. +$4000, +5 diversity",
    type: "action",
    rarity: "uncommon",
    effect: (r) => { r.funds += 4000; r.diversity = Math.min(100, r.diversity + 5); }
  },
  {
    id: "emergency_fund",
    title: "Emergency Reserve",
    art: "🏦💰",
    description: "Tap into reserves. +$4000 but -10 reputation (looks desperate)",
    type: "action",
    rarity: "common",
    effect: (r) => { r.funds += 4000; r.reputation = Math.max(0, r.reputation - 10); }
  },
];

// ============================================
// ENRICHMENT & TREAT CARDS - Horse Interaction
// ============================================
export const enrichmentCards: CardData[] = [
  {
    id: "salt_lick",
    title: "Mineral Salt Block",
    art: "🧂🐴",
    description: "Horses love it! +10 herd health, horses gather and show happiness.",
    type: "action",
    rarity: "common",
    cost: 300,
    effect: (r) => { 
      r.herdHealth = Math.min(100, r.herdHealth + 10); 
      r.funds -= 300;
    }
  },
  {
    id: "apple_treats",
    title: "Apple Treats",
    art: "🍎❤️",
    description: "Sweet treats for the herd! +5 herd health, +5 reputation (visitors love it)",
    type: "action",
    rarity: "common",
    cost: 150,
    effect: (r) => { 
      r.herdHealth = Math.min(100, r.herdHealth + 5); 
      r.reputation = Math.min(100, r.reputation + 5);
      r.funds -= 150;
    }
  },
  {
    id: "hay_supplement",
    title: "Premium Hay Delivery",
    art: "🌾🚛",
    description: "Extra nutrition for the herd. +20 forage, +5 herd health.",
    type: "action",
    rarity: "uncommon",
    cost: 800,
    effect: (r) => { 
      r.forage = Math.min(100, r.forage + 20); 
      r.herdHealth = Math.min(100, r.herdHealth + 5);
      r.funds -= 800;
    }
  },
  {
    id: "water_trough",
    title: "New Water Trough",
    art: "💧🪣",
    description: "Fresh clean water source. +15 water, +5 herd health.",
    type: "action",
    rarity: "common",
    cost: 500,
    effect: (r) => { 
      r.water = Math.min(100, r.water + 15); 
      r.herdHealth = Math.min(100, r.herdHealth + 5);
      r.funds -= 500;
    }
  },
  {
    id: "grooming_day",
    title: "Grooming Day",
    art: "✨🐴",
    description: "Volunteers brush the horses. +10 herd health, +10 reputation.",
    type: "action",
    rarity: "uncommon",
    effect: (r) => { 
      r.herdHealth = Math.min(100, r.herdHealth + 10); 
      r.reputation = Math.min(100, r.reputation + 10);
    }
  },
  {
    id: "shelter_upgrade",
    title: "Shelter Construction",
    art: "🏠🔨",
    description: "Build wind shelters. +15 herd health, protects from harsh weather.",
    type: "action",
    rarity: "rare",
    cost: 2500,
    effect: (r) => { 
      r.herdHealth = Math.min(100, r.herdHealth + 15); 
      r.funds -= 2500;
    }
  },
  {
    id: "carrot_bucket",
    title: "Carrot Bucket",
    art: "🥕🪣",
    description: "Horses go crazy for carrots! +8 herd health, great for photos (+8 rep).",
    type: "action",
    rarity: "common",
    cost: 200,
    effect: (r) => { 
      r.herdHealth = Math.min(100, r.herdHealth + 8); 
      r.reputation = Math.min(100, r.reputation + 8);
      r.funds -= 200;
    }
  },
  {
    id: "scratch_post",
    title: "Scratching Posts",
    art: "🪵😌",
    description: "Horses love to scratch! +5 herd health, reduces stress.",
    type: "action",
    rarity: "common",
    cost: 400,
    effect: (r) => { 
      r.herdHealth = Math.min(100, r.herdHealth + 5); 
      r.funds -= 400;
    }
  },
];

// ============================================
// CHALLENGING EVENT CARDS
// ============================================
export const challengeEventCards: CardData[] = [
  {
    id: "cattle_conflict",
    title: "Cattle Rancher Conflict",
    art: "🐄⚔️",
    description: "Ranchers want horses removed. -15 BLM relations, must pay $2000 or lose 3 horses.",
    type: "event",
    rarity: "uncommon",
    effect: (r) => {
      r.blmRelations = Math.max(0, r.blmRelations - 15);
      if (r.funds >= 2000) {
        r.funds -= 2000;
      } else {
        r.population = Math.max(5, r.population - 3);
      }
    }
  },
  {
    id: "poacher_threat",
    title: "Poacher Activity",
    art: "🔫😈",
    description: "Illegal activity reported. Pay $3000 for security or risk losing 2 horses.",
    type: "event",
    rarity: "rare",
    effect: (r) => {
      if (r.funds >= 3000) {
        r.funds -= 3000;
        r.reputation = Math.min(100, r.reputation + 5);
      } else {
        r.population = Math.max(5, r.population - 2);
      }
    }
  },
  {
    id: "fence_damage",
    title: "Fence Line Down",
    art: "🚧💥",
    description: "Horses escaping! Pay $1500 to repair or lose 2 horses and -10 BLM relations.",
    type: "event",
    rarity: "common",
    effect: (r) => {
      if (r.funds >= 1500) {
        r.funds -= 1500;
      } else {
        r.population = Math.max(5, r.population - 2);
        r.blmRelations = Math.max(0, r.blmRelations - 10);
      }
    }
  },
  {
    id: "lawsuit",
    title: "Environmental Lawsuit",
    art: "⚖️📄",
    description: "Sued over land use! Legal fees -$4000, -10 reputation.",
    type: "event",
    rarity: "rare",
    effect: (r) => {
      r.funds = Math.max(0, r.funds - 4000);
      r.reputation = Math.max(0, r.reputation - 10);
    }
  },
  {
    id: "volunteer_injury",
    title: "Volunteer Injured",
    art: "🤕🚑",
    description: "Accident on site. Medical costs -$2500, -5 reputation.",
    type: "event",
    rarity: "uncommon",
    effect: (r) => {
      r.funds = Math.max(0, r.funds - 2500);
      r.reputation = Math.max(0, r.reputation - 5);
    }
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

// Check if paint horses are unlocked
function isPaintUnlocked(): boolean {
  try {
    return localStorage.getItem('pmp_paint_unlocked') === 'true';
  } catch {
    return false;
  }
}

// Filter cards based on unlock status
function filterLockedCards(cards: CardData[]): CardData[] {
  const unlocked = isPaintUnlocked();
  return cards.filter(card => !card.requiresUnlock || unlocked);
}

// Get all cards for a specific HMA
export function getHMADeck(hmaId: string): CardData[] {
  const baseDeck = filterLockedCards([...pryorStarterDeck]);
  
  switch(hmaId) {
    case 'kiger':
      return [...baseDeck, ...filterLockedCards(kigerCards)];
    case 'mccullough':
      return [...baseDeck, ...filterLockedCards(mcculloughCards)];
    default:
      return baseDeck;
  }
}

// Get random card drop based on rarity weights
export function getRandomCardDrop(): CardData | null {
  const roll = Math.random() * 100;
  
  if (roll < 1) {
    // 1% legendary
    return legendaryCards[Math.floor(Math.random() * legendaryCards.length)];
  } else if (roll < 5) {
    // 4% rare
    return rareCards[Math.floor(Math.random() * rareCards.length)];
  } else if (roll < 15) {
    // 10% mechanic cards
    return mechanicCards[Math.floor(Math.random() * mechanicCards.length)];
  } else if (roll < 30) {
    // 15% enrichment/treat cards (new!)
    return enrichmentCards[Math.floor(Math.random() * enrichmentCards.length)];
  } else if (roll < 40) {
    // 10% silly/fun
    return sillyEventCards[Math.floor(Math.random() * sillyEventCards.length)];
  } else if (roll < 50) {
    // 10% seasonal
    return seasonalEventCards[Math.floor(Math.random() * seasonalEventCards.length)];
  }
  
  return null; // 50% no bonus drop (more cards overall)
}

// Get a crisis card (called during end year if conditions are bad)
export function getCrisisCard(): CardData | null {
  const roll = Math.random() * 100;
  
  // 25% base chance of crisis, modified by conditions
  if (roll < 25) {
    return crisisCards[Math.floor(Math.random() * crisisCards.length)];
  }
  return null;
}

// Get a challenge event card
export function getChallengeEvent(): CardData | null {
  const roll = Math.random() * 100;
  
  // 20% chance of challenge event
  if (roll < 20) {
    return challengeEventCards[Math.floor(Math.random() * challengeEventCards.length)];
  }
  return null;
}

// Legacy export for backwards compatibility
export const starterDeck = pryorStarterDeck;
